// A small markdown renderer, written rather than installed.
//
// What it has to render is what an agent writes into a task description and
// what a person types into a comment: paragraphs, headings, bold, italic, code,
// fenced blocks, lists, quotes and links. A library that also does tables,
// footnotes and HTML passthrough is 40kB and a much larger surface to have to
// trust.
//
// SAFETY. Every input is escaped to text *first*, and the formatting rules then
// run over already-escaped content and emit React elements — never
// dangerouslySetInnerHTML. So a description containing `<img onerror=...>` is
// displayed, not executed, and there is no ordering of rules that can undo
// that. Link targets are additionally restricted to http, https and mailto, so
// `[click](javascript:...)` renders as plain text.

import { type ReactNode, createElement } from "react"

import { isMentionRef, mentionLabel } from "../lib/mention-refs"

// Blocks a scheme we will not follow. Anything *without* a scheme is a
// relative link inside the wiki — the form every internal link takes — and is
// safe by construction, so the rule is a denylist of schemes rather than an
// allowlist of prefixes. The earlier allowlist silently turned every internal
// wiki link into plain text.
const UNSAFE_HREF = /^(?!https?:|mailto:)[a-z][a-z0-9+.-]*:/i

// LINK-PATTERN — pinned byte-for-byte against src/context-hub/links.ts by
// src/test/links.test.ts. The server rewrites hrefs when a page moves; if its
// idea of what a link is differs from this one by a single character, the two
// drift and a rewrite either misses a link or mangles text that was never one.
export const MARKDOWN_LINK = /(?<!!)\[([^\]\n]+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/

/** The alt Milkdown writes for a block image: its aspect ratio, `1.00`. */
const RATIO_ALT = /^\d+(?:\.\d+)?$/

const CODE_SPAN = /`[^`\n]+`/
const IMAGE = /!\[[^\]\n]*\]\([^)\s]+(?:\s+"[^"]*")?\)/
const BOLD = /\*\*[^*\n]+\*\*/
const STRIKE = /~~[^~\n]+~~/
const ITALIC = /\*[^*\n]+\*|_[^_\n]+_/

// Order is precedence. Code first, so `` `**not bold**` `` stays literal;
// images before links, because an image is a link with a bang in front and the
// link rule would otherwise eat it and leave the bang behind. Strike sits with
// bold for the same reason: both are runs of a doubled punctuation mark, and
// the italic rule would otherwise claim half of one.
const INLINE = new RegExp(
  [CODE_SPAN, IMAGE, BOLD, STRIKE, ITALIC, MARKDOWN_LINK].map((r) => `(${r.source})`).join("|"),
  "g",
)

/** Splits a table row on unescaped pipes, dropping the empty cells that a
 *  leading and trailing `|` produce. */
function cells(row: string): string[] {
  // Split on pipes that are not escaped, then unescape the ones that survived
  // inside a cell — `\|` is how a pipe is written in table content.
  const out = row.split(/(?<!\\)\|/).map((cell) => cell.replace(/\\\|/g, "|").trim())
  if (out[0] === "") out.shift()
  if (out.at(-1) === "") out.pop()
  return out
}

/** Turns an href written in the document into somewhere this app can go.
 *  Returning null means "not an internal link" and the href is used as-is. */
export type HrefResolver = (href: string) => string | null

/** Inline spans: code, images, bold, italic, links. */
function inline(text: string, keyPrefix: string, resolveHref?: HrefResolver): ReactNode[] {
  const out: ReactNode[] = []
  const pattern = new RegExp(INLINE.source, "g")

  let last = 0
  let match: RegExpExecArray | null
  let index = 0

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) out.push(text.slice(last, match.index))
    const token = match[0]
    const key = `${keyPrefix}-${index++}`

    if (token.startsWith("`")) {
      out.push(
        <code key={key} className="rounded bg-surface-muted px-1 py-0.5 text-[0.9em] text-foreground">
          {token.slice(1, -1)}
        </code>,
      )
    } else if (token.startsWith("![")) {
      const split = token.indexOf("](")
      const inside = token.slice(split + 2, -1)
      // `![alt](src "title")`. The title is optional in markdown generally, and
      // never optional in what the block editor writes: Milkdown puts an
      // image's caption there.
      const titled = /^(\S+)\s+"([^"]*)"$/.exec(inside)
      const written = titled?.[1] ?? inside
      const caption = titled?.[2] ?? ""

      // Milkdown's image-block schema stores the image's aspect *ratio* in the
      // alt slot — every block image serialises as `![1.00](…)`. So a bare
      // number is not alt text, and showing it as one reads as a bug.
      const declared = token.slice(2, split)
      const alt = caption || (RATIO_ALT.test(declared) ? "" : declared)

      // Resolved like a link is. A comment stores an uploaded image as
      // `attachment:<id>` rather than a pre-signed URL, because a signed URL
      // expires in an hour and the body does not.
      const src = resolveHref?.(written) ?? written
      // Same scheme restriction as a link. An imported page can carry an image
      // pointing anywhere, and `javascript:` in an <img> src is inert but
      // `data:` carrying SVG is not. An `attachment:` ref that resolved to
      // nothing lands here too, and says so rather than showing a broken box.
      out.push(
        !UNSAFE_HREF.test(src) ? (
          <img
            key={key}
            src={src}
            alt={alt}
            loading="lazy"
            className="my-2 max-w-full rounded-md border border-border"
            // A broken image draws the browser's own icon, which looks like the
            // page is broken rather than the file being gone.
            onError={(event) => {
              const el = event.currentTarget
              el.replaceWith(
                Object.assign(document.createElement("span"), {
                  className: "text-xs text-faint",
                  textContent: alt ? `[image unavailable: ${alt}]` : "[image unavailable]",
                }),
              )
            }}
          />
        ) : (
          <span key={key} className="text-xs text-faint">
            {alt ? `[image unavailable: ${alt}]` : "[image unavailable]"}
          </span>
        ),
      )
    } else if (token.startsWith("**")) {
      out.push(
        <strong key={key} className="font-semibold text-foreground">
          {token.slice(2, -2)}
        </strong>,
      )
    } else if (token.startsWith("~~")) {
      out.push(
        <s key={key} className="opacity-70">
          {token.slice(2, -2)}
        </s>,
      )
    } else if (token.startsWith("[")) {
      const split = token.indexOf("](")
      const label = token.slice(1, split)
      const href = token.slice(split + 2, -1)
      // Mentions are rendered as pills, not links — there is no person page to
      // link to, and a dead link is worse than a chip. The label is a snapshot
      // of the person's name when the comment was written; the id is
      // authoritative.
      if (isMentionRef(href)) {
        out.push(
          <span
            key={key}
            className="inline-flex items-center rounded-full bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent"
          >
            {mentionLabel(label)}
          </span>,
        )
      } else {
        // An unsafe scheme is shown as what it is rather than silently dropped —
        // a link that vanishes looks like a rendering bug.
        const resolved = resolveHref?.(href) ?? null
        const target = resolved ?? (UNSAFE_HREF.test(href) ? null : href)
        out.push(
          target !== null ? (
            <a
              key={key}
              href={target}
              // Only leave the app for a link that leaves the wiki. An internal
              // one resolved to a route should open in this tab.
              {...(resolved ? {} : { target: "_blank", rel: "noopener noreferrer" })}
              className="text-interactive underline underline-offset-2"
            >
              {label || href}
            </a>
          ) : (
            <span key={key}>{token}</span>
          ),
        )
      }
    } else {
      out.push(
        <em key={key} className="italic">
          {token.slice(1, -1)}
        </em>,
      )
    }
    last = pattern.lastIndex
  }

  if (last < text.length) out.push(text.slice(last))
  return out
}

type Block = {
  kind: "p" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "quote" | "code" | "ul" | "ol" | "tasks" | "hr" | "table"
  lines: string[]
}

/** Three or more of `-`, `*` or `_` alone on a line: a thematic break, which
 *  is what the block editor's Divider writes. */
const THEMATIC_BREAK = /^(?:-{3,}|\*{3,}|_{3,})$/

/** A line that is only a line break. Milkdown serialises an empty paragraph
 *  this way to preserve a blank line across its own round trip;
 *  stripEmptyLineMarkers keeps it out of anything we store, and this is what
 *  reads the comments written before that existed. */
const EMPTY_LINE_MARKER = /^<br\s*\/?>$/i

/** `[ ] ` or `[x] ` at the head of a list item. */
const TASK_ITEM = /^\[([ xX])\]\s+(.*)$/

/** `|---|:--:|` — the row that turns the line above it into a table header.
 *  Without it a line of prose containing a pipe would start a table. */
const DELIMITER_ROW = /^\|?(\s*:?-{2,}:?\s*\|)*\s*:?-{2,}:?\s*\|?$/

function blocks(source: string): Block[] {
  const lines = source.replaceAll("\r\n", "\n").split("\n")
  const out: Block[] = []
  let fence: Block | null = null

  for (let cursor = 0; cursor < lines.length; cursor++) {
    const line = lines[cursor] ?? ""

    // A table is the only block that needs to see the next line before it can
    // tell what this one is, so it is matched here rather than in the chain
    // below. Outside a fence only — a pipe table inside ``` is just text.
    if (!fence && line.trim().startsWith("|") && DELIMITER_ROW.test((lines[cursor + 1] ?? "").trim())) {
      const table: string[] = [line.trim()]
      cursor += 2 // the header is consumed, and the delimiter is not kept
      while (cursor < lines.length && (lines[cursor] ?? "").trim().startsWith("|")) {
        table.push((lines[cursor] ?? "").trim())
        cursor++
      }
      cursor-- // the for-loop's own increment steps past the terminating line
      out.push({ kind: "table", lines: table })
      continue
    }

    if (line.trimStart().startsWith("```")) {
      if (fence) {
        out.push(fence)
        fence = null
      } else {
        fence = { kind: "code", lines: [] }
      }
      continue
    }
    // Inside a fence every line is literal, blank ones included.
    if (fence) {
      fence.lines.push(line)
      continue
    }

    const trimmed = line.trim()
    const last = out.at(-1)

    // Treated as the blank line it stands for. Not a door into rendering HTML:
    // this is one literal, and every other tag still escapes to text.
    if (!trimmed || EMPTY_LINE_MARKER.test(trimmed)) {
      // A blank line ends whatever was open, which is what separates two
      // adjacent lists rather than merging them.
      if (last) out.push({ kind: "p", lines: [] })
      continue
    }

    if (THEMATIC_BREAK.test(trimmed)) {
      // A rule has no content, but blocks with no lines are dropped below, so
      // it carries a placeholder. Deliberate over `---` under a paragraph
      // being a setext h2: this renderer has never done setext, so that input
      // renders as literal text today and a rule is strictly closer.
      out.push({ kind: "hr", lines: ["-"] })
      continue
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(trimmed)
    if (heading) {
      out.push({ kind: `h${(heading[1] ?? "#").length}` as Block["kind"], lines: [heading[2] ?? ""] })
      continue
    }

    const bullet = /^[-*+]\s+(.*)$/.exec(trimmed)
    if (bullet) {
      const item = bullet[1] ?? ""
      // `- [ ] thing` is a checklist, not a bullet whose text opens with a
      // bracket. Its own kind, so a checklist and a plain list written next to
      // each other do not merge into one.
      const kind = TASK_ITEM.test(item) ? "tasks" : "ul"
      if (last?.kind === kind) last.lines.push(item)
      else out.push({ kind, lines: [item] })
      continue
    }

    const numbered = /^\d+[.)]\s+(.*)$/.exec(trimmed)
    if (numbered) {
      if (last?.kind === "ol") last.lines.push(numbered[1] ?? "")
      else out.push({ kind: "ol", lines: [numbered[1] ?? ""] })
      continue
    }

    if (trimmed.startsWith(">")) {
      const body = trimmed.replace(/^>\s?/, "")
      if (last?.kind === "quote") last.lines.push(body)
      else out.push({ kind: "quote", lines: [body] })
      continue
    }

    // Wrapped prose joins the paragraph above it.
    if (last?.kind === "p" && last.lines.length) last.lines.push(trimmed)
    else out.push({ kind: "p", lines: [trimmed] })
  }

  // An unterminated fence still renders as code — the alternative is losing the
  // text entirely because someone forgot the closing backticks.
  if (fence) out.push(fence)
  return out.filter((block) => block.lines.length > 0)
}

// h4 and below stay the size of h3 and lean on weight and colour instead. The
// block editor's / menu offers all six, and a comment is not a document —
// rendering an h6 smaller than the prose around it reads as a mistake.
const HEADING_CLASS = {
  h1: "mt-4 mb-1 text-base font-semibold text-foreground first:mt-0",
  h2: "mt-4 mb-1 text-sm font-semibold text-foreground first:mt-0",
  h3: "mt-3 mb-1 text-sm font-medium text-foreground first:mt-0",
  h4: "mt-3 mb-1 text-sm font-medium text-foreground first:mt-0",
  h5: "mt-3 mb-1 text-sm font-medium text-muted first:mt-0",
  h6: "mt-3 mb-1 text-sm font-medium text-faint first:mt-0",
} as const

// `children` is optional because several callers hold a field the API types
// as optional. The body already guards, so widening the prop is the honest
// signature rather than making every call site write `?? ""`.
export function Markdown({
  children,
  className = "",
  resolveHref,
}: {
  children?: string
  className?: string
  /** Wiki pages pass one so an internal link navigates inside the app rather
   *  than at a path the router knows nothing about. */
  resolveHref?: HrefResolver
}) {
  const parsed = blocks(children ?? "")
  const md = (text: string, key: string) => inline(text, key, resolveHref)

  return (
    <div className={`text-sm leading-relaxed text-muted ${className}`}>
      {parsed.map((block, index) => {
        const key = `b${index}`

        if (block.kind === "code") {
          return (
            <pre
              key={key}
              className="my-2 overflow-x-auto rounded-md border border-border bg-surface-muted p-3 text-xs text-foreground"
            >
              <code>{block.lines.join("\n")}</code>
            </pre>
          )
        }

        if (block.kind === "table") {
          const [header, ...rows] = block.lines
          const head = cells(header ?? "")
          return (
            // Scrolls inside its own box. A wide table out of a Confluence
            // export would otherwise make the whole page scroll sideways.
            <div key={key} className="my-3 overflow-x-auto rounded-md border border-border">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-border bg-surface-muted">
                    {head.map((cell, i) => (
                      <th key={i} className="px-3 py-2 text-left font-medium text-foreground">
                        {md(cell, `${key}-h${i}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, r) => {
                    const values = cells(row)
                    return (
                      <tr key={r} className="border-b border-border last:border-0">
                        {/* Padded to the header's width. A short row in the
                            source would otherwise collapse the columns after
                            it and misalign everything below. */}
                        {head.map((_, c) => (
                          <td key={c} className="px-3 py-2 align-top text-muted">
                            {md(values[c] ?? "", `${key}-${r}-${c}`)}
                          </td>
                        ))}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )
        }

        if (block.kind === "hr") {
          return <hr key={key} className="my-4 border-0 border-t border-border" />
        }

        if (block.kind === "tasks") {
          return (
            <ul key={key} className="my-2 flex flex-col gap-1">
              {block.lines.map((line, i) => {
                const item = TASK_ITEM.exec(line)
                return (
                  <li key={i} className="flex items-start gap-2">
                    {/* Not interactive: the state lives in the markdown, and
                        this component never writes. */}
                    <input
                      type="checkbox"
                      checked={(item?.[1] ?? " ") !== " "}
                      readOnly
                      disabled
                      className="mt-1 shrink-0"
                    />
                    <span>{md(item?.[2] ?? line, `${key}-${i}`)}</span>
                  </li>
                )
              })}
            </ul>
          )
        }

        if (block.kind === "ul" || block.kind === "ol") {
          const Tag = block.kind
          return createElement(
            Tag,
            {
              key,
              className: `my-2 flex flex-col gap-1 pl-5 ${
                Tag === "ul" ? "list-disc" : "list-decimal"
              } marker:text-faint`,
            },
            block.lines.map((line, i) => <li key={i}>{md(line, `${key}-${i}`)}</li>),
          )
        }

        if (block.kind === "quote") {
          return (
            <blockquote
              key={key}
              className="my-2 border-l-2 border-border-strong pl-3 text-muted italic"
            >
              {md(block.lines.join(" "), key)}
            </blockquote>
          )
        }

        if (block.kind !== "p") {
          return createElement(
            block.kind,
            { key, className: HEADING_CLASS[block.kind] },
            md(block.lines[0] ?? "", key),
          )
        }

        return (
          <p key={key} className="my-2 first:mt-0 last:mb-0">
            {md(block.lines.join(" "), key)}
          </p>
        )
      })}
    </div>
  )
}
